/**
 * Generates PDF for Claim Form A (Reimbursement Insurance) by writing directly on PDF template
 * Uses pdf-lib to write data directly onto the PDF form
 */

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// Import the PDF template
import pdfTemplate from './pdf/Final-Form-A.pdf';

// Helper to format date as DD/MM/YY or DD/MM/YYYY
const formatDate = (dateStr, fullYear = false) => {
  if (!dateStr) return '';
  try {
    let date;
    if (dateStr.includes('/')) {
      // Already in DD/MM/YYYY format
      return dateStr;
    } else if (dateStr.includes('T') || dateStr.includes('-')) {
      // ISO format
      date = new Date(dateStr);
    } else {
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) return dateStr;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = fullYear ? String(date.getFullYear()) : String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

// Helper function to convert ISO date to DD/MM/YYYY format
const formatISODate = (isoDate) => {
  if (!isoDate) return '';
  try {
    // If already in DD/MM/YYYY format, return as is
    if (typeof isoDate === 'string' && isoDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return isoDate;
    }
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return isoDate;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${day}/${month}/${year}`;
  } catch {
    return isoDate;
  }
};

// Helper function to pad amount with leading zeros to 7 digits
const padAmount = (amount) => {
  if (!amount) return '';
  const numStr = String(amount).replace(/[^0-9]/g, '');
  if (!numStr) return '';
  return numStr.padStart(7, '0');
};

// Convert API response structure to the format expected by the PDF writer
const transformData = (inputData) => {
  // Handle different input formats
  let claimFormA;
  
  if (inputData.success && inputData.data) {
    // API response format with success wrapper
    claimFormA = Array.isArray(inputData.data) ? inputData.data[0] : inputData.data;
  } else if (inputData.claimForm) {
    // Direct claimForm format (like the test data)
    claimFormA = inputData.claimForm;
  } else {
    // Direct form data
    claimFormA = inputData;
  }

  // Map from API/Form state to PDF expected format
  return {
    // Section A - Primary Insured / TPA Details
    primaryInsured: {
      policyNo: claimFormA.primaryInsured?.policyNo || claimFormA.policyNo || claimFormA.patientDetails?.policyNo || '',
      certificateNo: claimFormA.primaryInsured?.certificateNo || claimFormA.certificateNo || claimFormA.patientDetails?.certificateNo || '',
      companyTpaId: claimFormA.primaryInsured?.companyTpaId || claimFormA.companyTpaId || '',
      name: claimFormA.primaryInsured?.name || claimFormA.insuredName || claimFormA.patientDetails?.name || '',
      address: claimFormA.primaryInsured?.address || claimFormA.insuredAddress || claimFormA.patientDetails?.address || '',
      address2: claimFormA.primaryInsured?.address2 || claimFormA.patientDetails?.address2 || '',
      city: claimFormA.primaryInsured?.city || claimFormA.insuredCity || claimFormA.patientDetails?.city || '',
      state: claimFormA.primaryInsured?.state || claimFormA.insuredState || claimFormA.patientDetails?.state || '',
      pinCode: claimFormA.primaryInsured?.pinCode || claimFormA.insuredPinCode || claimFormA.patientDetails?.pinCode || '',
      phone: claimFormA.primaryInsured?.phone || claimFormA.insuredPhone || claimFormA.patientDetails?.contactNo || '',
      email: claimFormA.primaryInsured?.email || claimFormA.insuredEmail || claimFormA.patientDetails?.email || ''
    },
    
    // Insurance Details
    insuranceDetails: {
      tpaName: claimFormA.insuranceDetails?.tpaName || claimFormA.companyTpaId || '',
      insurerName: claimFormA.insuranceDetails?.insurerName || '',
      policyType: claimFormA.insuranceDetails?.policyType || '',
      sumInsured: claimFormA.insuranceDetails?.sumInsured || '',
      claimNo: claimFormA.insuranceDetails?.claimNo || ''
    },
    
    // Section B - Insurance History
    insuranceHistory: {
      currentlyCoveredByOther: claimFormA.insuranceHistory?.coveredByOtherInsurance === 'yes' || 
                               claimFormA.insuranceHistory?.currentlyCoveredByOther === true ||
                               claimFormA.coveredByOtherInsurance === 'yes',
      firstInsuranceDate: formatISODate(claimFormA.insuranceHistory?.firstInsuranceCommencementDate || 
                                        claimFormA.insuranceHistory?.firstInsuranceDate ||
                                        claimFormA.firstInsuranceCommencementDate),
      otherInsuranceCompanyName: claimFormA.insuranceHistory?.otherCompanyName || 
                                  claimFormA.insuranceHistory?.otherInsuranceCompanyName ||
                                  claimFormA.otherCompanyName || '',
      otherPolicyNo: claimFormA.insuranceHistory?.otherPolicyNo || claimFormA.otherPolicyNo || '',
      sumInsuredRs: padAmount(claimFormA.insuranceHistory?.sumInsured || 
                          claimFormA.insuranceHistory?.sumInsuredRs || 
                          claimFormA.sumInsured || ''),
      hospitalizedInLastFourYears: claimFormA.insuranceHistory?.hospitalizedInLastFourYears === 'yes' ||
                                    claimFormA.insuranceHistory?.hospitalizedInLastFourYears === true ||
                                    claimFormA.hospitalizedInLastFourYears === 'yes',
      hospitalizationDate: formatISODate(claimFormA.insuranceHistory?.lastHospitalizationDate ||
                                         claimFormA.insuranceHistory?.hospitalizationDate ||
                                         claimFormA.lastHospitalizationDate),
      diagnosisDetails: claimFormA.insuranceHistory?.lastHospitalizationDiagnosis ||
                        claimFormA.insuranceHistory?.diagnosisDetails ||
                        claimFormA.lastHospitalizationDiagnosis || '',
      previouslyCoveredByOther: claimFormA.insuranceHistory?.previouslyCovered === 'yes' ||
                                 claimFormA.insuranceHistory?.previouslyCoveredByOther === true ||
                                 claimFormA.previouslyCovered === 'yes',
      previousCompanyName: claimFormA.insuranceHistory?.previousCompanyName || claimFormA.previousCompanyName || ''
    },
    
    // Section C - Patient Details
    patientDetails: {
      name: claimFormA.patientDetails?.name || claimFormA.patientDetails?.patientName || claimFormA.patientName || '',
      gender: (claimFormA.patientDetails?.gender || claimFormA.patientDetails?.patientGender || claimFormA.patientGender || '').charAt(0).toUpperCase() + 
              (claimFormA.patientDetails?.gender || claimFormA.patientDetails?.patientGender || claimFormA.patientGender || '').slice(1).toLowerCase(),
      age: String(claimFormA.patientDetails?.age || claimFormA.patientDetails?.patientAge || claimFormA.patientAge || ''),
      ageMonths: String(claimFormA.patientDetails?.ageMonths || claimFormA.patientDetails?.patientAgeMonths || claimFormA.patientAgeMonths || ''),
      dateOfBirth: formatISODate(claimFormA.patientDetails?.dob || claimFormA.patientDetails?.patientDob || 
                                 claimFormA.patientDetails?.dateOfBirth || claimFormA.patientDob),
      relationship: claimFormA.patientDetails?.relationshipToPrimary || claimFormA.patientDetails?.relationship || 
                    claimFormA.relationshipToPrimary || 'Self',
      relationshipOther: claimFormA.patientDetails?.relationshipOther || claimFormA.relationshipOther || '',
      occupation: claimFormA.patientDetails?.occupation || claimFormA.patientDetails?.patientOccupation || 
                  claimFormA.patientOccupation || '',
      occupationOther: claimFormA.patientDetails?.occupationOther || claimFormA.patientDetails?.patientOccupationOther ||
                       claimFormA.patientOccupationOther || '',
      address: claimFormA.patientDetails?.address || claimFormA.patientDetails?.patientAddress || claimFormA.patientAddress || '',
      address2: claimFormA.patientDetails?.address2 || '',
      city: claimFormA.patientDetails?.city || claimFormA.patientDetails?.patientCity || claimFormA.patientCity || '',
      state: claimFormA.patientDetails?.state || claimFormA.patientDetails?.patientState || claimFormA.patientState || '',
      pinCode: claimFormA.patientDetails?.pinCode || claimFormA.patientDetails?.patientPinCode || claimFormA.patientPinCode || '',
      phoneNo: claimFormA.patientDetails?.phone || claimFormA.patientDetails?.patientPhone || claimFormA.patientPhone || '',
      email: claimFormA.patientDetails?.email || claimFormA.patientDetails?.patientEmail || claimFormA.patientEmail || ''
    },
    
    // Section D - Hospitalization Details
    hospitalizationDetails: {
      hospitalName: claimFormA.hospitalizationDetails?.hospitalName || claimFormA.hospitalName || '',
      roomCategory: claimFormA.hospitalizationDetails?.roomCategory || claimFormA.roomCategory || '',
      hospitalizationDueTo: claimFormA.hospitalizationDetails?.hospitalizationDueTo || claimFormA.hospitalizationDueTo || '',
      dateOfInjuryOrDisease: formatISODate(claimFormA.hospitalizationDetails?.injuryOrDiseaseDate ||
                                           claimFormA.hospitalizationDetails?.dateOfInjuryOrDisease ||
                                           claimFormA.injuryOrDiseaseDate),
      dateOfDelivery: formatISODate(claimFormA.hospitalizationDetails?.dateOfDelivery || claimFormA.dateOfDelivery),
      dateOfAdmission: formatISODate(claimFormA.hospitalizationDetails?.dateOfAdmission || claimFormA.dateOfAdmission),
      timeOfAdmission: claimFormA.hospitalizationDetails?.timeOfAdmission || claimFormA.timeOfAdmission || '',
      dateOfDischarge: formatISODate(claimFormA.hospitalizationDetails?.dateOfDischarge || claimFormA.dateOfDischarge),
      timeOfDischarge: claimFormA.hospitalizationDetails?.timeOfDischarge || claimFormA.timeOfDischarge || '',
      injuryCause: claimFormA.hospitalizationDetails?.injuryCause || claimFormA.injuryCause || '',
      isMedicoLegal: claimFormA.hospitalizationDetails?.isMedicoLegal === 'yes' || 
                     claimFormA.hospitalizationDetails?.isMedicoLegal === true ||
                     claimFormA.isMedicoLegal === 'yes',
      reportedToPolice: claimFormA.hospitalizationDetails?.reportedToPolice === 'yes' ||
                        claimFormA.hospitalizationDetails?.reportedToPolice === true ||
                        claimFormA.reportedToPolice === 'yes',
      mlcReportAttached: claimFormA.hospitalizationDetails?.mlcReportAttached === 'yes' ||
                         claimFormA.hospitalizationDetails?.mlcReportAttached === true ||
                         claimFormA.mlcReportAttached === 'yes',
      systemOfMedicine: claimFormA.hospitalizationDetails?.systemOfMedicine || claimFormA.systemOfMedicine || '',
      // Diagnosis fields for current hospitalization
      diagnosis: claimFormA.hospitalizationDetails?.diagnosis || claimFormA.diagnosis || '',
      diagnosisIcdCode: claimFormA.hospitalizationDetails?.diagnosisIcdCode || claimFormA.diagnosisIcdCode || ''
    },
    
    // Section E - Claim/Expense Details
    expenseDetails: {
      preHospitalizationExpenses: padAmount(claimFormA.claimDetails?.preHospitalizationExpenses || 
                                         claimFormA.expenseDetails?.preHospitalizationExpenses ||
                                         claimFormA.preHospitalizationExpenses || ''),
      hospitalizationExpenses: padAmount(claimFormA.claimDetails?.hospitalizationExpenses ||
                                      claimFormA.expenseDetails?.hospitalizationExpenses ||
                                      claimFormA.hospitalizationExpenses || ''),
      postHospitalizationExpenses: padAmount(claimFormA.claimDetails?.postHospitalizationExpenses ||
                                          claimFormA.expenseDetails?.postHospitalizationExpenses ||
                                          claimFormA.postHospitalizationExpenses || ''),
      healthCheckUpCost: padAmount(claimFormA.claimDetails?.healthCheckupCost ||
                                claimFormA.expenseDetails?.healthCheckUpCost ||
                                claimFormA.healthCheckupCost || ''),
      ambulanceCharges: padAmount(claimFormA.claimDetails?.ambulanceCharges ||
                               claimFormA.expenseDetails?.ambulanceCharges ||
                               claimFormA.ambulanceCharges || ''),
      othersCode: claimFormA.claimDetails?.otherExpensesCode ||
                  claimFormA.expenseDetails?.othersCode ||
                  claimFormA.otherExpensesCode || '',
      othersAmount: padAmount(claimFormA.claimDetails?.otherExpenses ||
                           claimFormA.expenseDetails?.othersAmount ||
                           claimFormA.otherExpenses || ''),
      totalExpenses: padAmount(claimFormA.claimDetails?.totalClaimAmount ||
                            claimFormA.expenseDetails?.totalExpenses ||
                            claimFormA.totalClaimAmount || ''),
      preHospitalizationPeriodDays: String(claimFormA.claimDetails?.preHospitalizationPeriod ||
                                           claimFormA.expenseDetails?.preHospitalizationPeriodDays ||
                                           claimFormA.preHospitalizationPeriod || ''),
      postHospitalizationPeriodDays: String(claimFormA.claimDetails?.postHospitalizationPeriod ||
                                            claimFormA.expenseDetails?.postHospitalizationPeriodDays ||
                                            claimFormA.postHospitalizationPeriod || ''),
      domiciliaryHospitalization: claimFormA.claimDetails?.claimForDomiciliary === 'yes' ||
                                   claimFormA.expenseDetails?.domiciliaryHospitalization === true ||
                                   claimFormA.claimForDomiciliary === 'yes',
      // Lump sum benefits - also pad with 7 digits
      hospitalDailyCash: padAmount(claimFormA.claimDetails?.lumpSumBenefits?.hospitalDailyCash ||
                                claimFormA.expenseDetails?.hospitalDailyCash ||
                                claimFormA.hospitalDailyCash || ''),
      surgicalCash: padAmount(claimFormA.claimDetails?.lumpSumBenefits?.surgicalCash ||
                           claimFormA.expenseDetails?.surgicalCash ||
                           claimFormA.surgicalCash || ''),
      criticalIllnessBenefit: padAmount(claimFormA.claimDetails?.lumpSumBenefits?.criticalIllnessBenefit ||
                                     claimFormA.expenseDetails?.criticalIllnessBenefit ||
                                     claimFormA.criticalIllnessBenefit || ''),
      convalescence: padAmount(claimFormA.claimDetails?.lumpSumBenefits?.convalescence ||
                            claimFormA.expenseDetails?.convalescence ||
                            claimFormA.convalescence || ''),
      prePostHospLumpSum: padAmount(claimFormA.claimDetails?.lumpSumBenefits?.prePostHospitalizationLumpSum ||
                                 claimFormA.expenseDetails?.prePostHospLumpSum ||
                                 claimFormA.prePostHospitalizationLumpSum || ''),
      otherLumpSumCode: claimFormA.claimDetails?.lumpSumBenefits?.othersCode ||
                        claimFormA.expenseDetails?.otherLumpSumCode || '',
      othersLumpSum: padAmount(claimFormA.claimDetails?.lumpSumBenefits?.others ||
                            claimFormA.expenseDetails?.othersLumpSum ||
                            claimFormA.othersLumpSum || ''),
      totalLumpSum: padAmount(claimFormA.claimDetails?.lumpSumBenefits?.total ||
                           claimFormA.expenseDetails?.totalLumpSum ||
                           claimFormA.totalLumpSum || '')
    },
    
    // Claim Documents Checklist
    claimDocuments: {
      claimFormSigned: claimFormA.documents?.claimFormSigned || claimFormA.claimDocuments?.claimFormSigned || false,
      claimIntimationCopy: claimFormA.documents?.claimIntimationCopy || claimFormA.claimDocuments?.claimIntimationCopy || false,
      hospitalMainBill: claimFormA.documents?.hospitalMainBill || claimFormA.claimDocuments?.hospitalMainBill || false,
      hospitalBreakupBill: claimFormA.documents?.hospitalBreakupBill || claimFormA.claimDocuments?.hospitalBreakupBill || false,
      hospitalBillPaymentReceipt: claimFormA.documents?.hospitalBillPaymentReceipt || claimFormA.claimDocuments?.hospitalBillPaymentReceipt || false,
      hospitalDischargeSummary: claimFormA.documents?.hospitalDischargeSummary || claimFormA.claimDocuments?.hospitalDischargeSummary || false,
      pharmacyBill: claimFormA.documents?.pharmacyBill || claimFormA.claimDocuments?.pharmacyBill || false,
      operationTheaterNotes: claimFormA.documents?.operationTheaterNotes || claimFormA.claimDocuments?.operationTheaterNotes || false,
      ecg: claimFormA.documents?.ecg || claimFormA.claimDocuments?.ecg || false,
      doctorRequestForInvestigation: claimFormA.documents?.doctorsRequestForInvestigation || 
                                      claimFormA.claimDocuments?.doctorRequestForInvestigation || false,
      investigationReports: claimFormA.documents?.investigationReports || claimFormA.claimDocuments?.investigationReports || false,
      doctorsPrescriptions: claimFormA.documents?.doctorsPrescriptions || claimFormA.claimDocuments?.doctorsPrescriptions || false,
      others: claimFormA.documents?.others || claimFormA.claimDocuments?.others || false,
      othersSpecify: claimFormA.documents?.othersSpecify || claimFormA.claimDocuments?.othersSpecify || ''
    },
    
    // Section F - Bills Enclosed (transform dates from ISO to DD/MM/YYYY)
    billsEnclosed: (claimFormA.billsEnclosed || claimFormA.bills || []).map(bill => ({
      ...bill,
      date: formatISODate(bill.date)
    })),
    
    // Section G - Bank Details
    bankDetails: {
      pan: claimFormA.bankAccount?.pan || claimFormA.bankDetails?.pan || claimFormA.pan || '',
      accountNumber: claimFormA.bankAccount?.accountNumber || claimFormA.bankDetails?.accountNumber || claimFormA.accountNumber || '',
      bankName: claimFormA.bankAccount?.bankName || claimFormA.bankDetails?.bankName || claimFormA.bankName || '',
      branchName: claimFormA.bankAccount?.bankBranch || claimFormA.bankDetails?.branchName || claimFormA.bankBranch || '',
      chequeOrDDPayableDetails: claimFormA.bankAccount?.chequePayableDetails || 
                                 claimFormA.bankDetails?.chequeOrDDPayableDetails ||
                                 claimFormA.chequePayableDetails || '',
      ifscCode: claimFormA.bankAccount?.ifscCode || claimFormA.bankDetails?.ifscCode || claimFormA.ifscCode || ''
    },
    
    // Section H - Declaration
    declaration: {
      date: formatISODate(claimFormA.declaration?.date || claimFormA.declarationDate),
      place: claimFormA.declaration?.place || claimFormA.declarationPlace || ''
    }
  };
};

/**
 * Generate the PDF by writing directly on the template
 * @param {Object} inputData - The form data in your JSON structure
 * @returns {Promise<Uint8Array>} - PDF bytes
 */
export const generateClaimFormAPdf = async (inputData) => {
  try {
    // Transform the data to expected format
    const data = transformData(inputData);
    
    // Debug: Log the transformed data
    console.log('🔄 Transformed data for Form A PDF generation:', {
      primaryInsured: data.primaryInsured.name,
      patientName: data.patientDetails.name,
      hospitalName: data.hospitalizationDetails.hospitalName,
      totalExpenses: data.expenseDetails.totalExpenses,
      documents: data.claimDocuments
    });

    // Fetch the PDF template
    const existingPdfBytes = await fetch(pdfTemplate).then(res => res.arrayBuffer());
    
    // Load the PDF
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    
    // Embed fonts
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Get pages
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    const secondPage = pages[1];
    const { width, height } = firstPage.getSize();

    // Helper function to draw text at specific position on a given page
    const drawTextOnPage = (page, text, x, y, options = {}) => {
      const textStr = String(text || '');
      page.drawText(textStr, {
        x,
        y,
        size: options.size || 8,
        font: options.bold ? boldFont : font,
        color: rgb(0, 0, 0),
      });
    };

    // Helper function to draw text on first page
    const drawText = (text, x, y, options = {}) => {
      drawTextOnPage(firstPage, text, x, y, options);
    };

    // Helper to draw text in character boxes
    const drawCharacterBoxesOnPage = (page, text, startX, y, charWidthOrOptions = 10.75, options = {}) => {
      let charWidth = charWidthOrOptions;
      let opts = options;
      let customSpacing = false;
      
      if (typeof charWidthOrOptions === 'object') {
        charWidth = 10.75;
        opts = charWidthOrOptions;
      } else if (typeof charWidthOrOptions === 'number' && charWidthOrOptions !== 10.75) {
        customSpacing = true;
      }
      
      const textStr = String(text || '').toUpperCase();
      let currentX = charWidth;
      
      for (let i = 0; i < textStr.length; i++) {
        const char = textStr[i];
        const isNumeric = /[0-9]/.test(char);
        const isSpace = char === ' ';
        
        let charSpacing;
        if (customSpacing) {
          charSpacing = charWidth;
        } else {
          charSpacing = 12.19;
          if (isNumeric) {
            charSpacing = 12.1;
          } else if (isSpace) {
            charSpacing = 11.99;
          }
        }
        
        if (i > 0) {
          currentX += charSpacing;
        }
        
        drawTextOnPage(page, char, startX + currentX, y, opts);
      }
    };

    // Helper for first page
    const drawCharacterBoxes = (text, startX, y, charWidthOrOptions = 10.75, options = {}) => {
      drawCharacterBoxesOnPage(firstPage, text, startX, y, charWidthOrOptions, options);
    };

    // Helper to draw a checkbox tick mark
    const drawCheckboxOnPage = (page, isChecked, x, y, options = {}) => {
      if (isChecked) {
        const size = options.size || 8;
        page.drawLine({
          start: { x: x + 1, y: y + size/2 },
          end: { x: x + size/3, y: y + 1 },
          thickness: 1.5,
          color: rgb(0, 0, 0),
        });
        page.drawLine({
          start: { x: x + size/3, y: y + 1 },
          end: { x: x + size - 1, y: y + size - 1 },
          thickness: 1.5,
          color: rgb(0, 0, 0),
        });
      }
    };

    // Helper for first page checkbox
    const drawCheckbox = (isChecked, x, y, options = {}) => {
      drawCheckboxOnPage(firstPage, isChecked, x, y, options);
    };

    // ============ PAGE 1 ============

    // ============ SECTION A - HOSPITAL/TPA DETAILS ============
    const primaryInsured = data.primaryInsured;
    const insuranceDetails = data.insuranceDetails;
    
    // Policy No
    drawCharacterBoxes(primaryInsured.policyNo, 55, 769, { size: 7 });
    
    // Certificate No
    drawCharacterBoxes(primaryInsured.certificateNo, 367, 769, { size: 7 });
    
    // TPA Name / Company ID
    drawCharacterBoxes(insuranceDetails.tpaName || primaryInsured.companyTpaId, 92, 756, { size: 7 });
    
    // Name of Primary Insured
    drawCharacterBoxes(primaryInsured.name, 55, 742, { size: 7 });
    
    // Address Line 1
    drawCharacterBoxes(primaryInsured.address, 55, 729, { size: 7 });
    
    // Address Line 2
    drawCharacterBoxes(primaryInsured.address2, 55, 716, { size: 7 });
    
    // City
    drawCharacterBoxes(primaryInsured.city, 67, 703, { size: 7 });
    
    // State
    drawCharacterBoxes(primaryInsured.state, 318, 703, { size: 7 });
    
    // Pin Code
    drawCharacterBoxes(primaryInsured.pinCode, 80, 689, { size: 7 });
    
    // Phone/Contact No
    drawCharacterBoxes(primaryInsured.phone, 200, 689, { size: 7 });
    
    // Email - display after phone (using regular text as email may be long)
    drawText(primaryInsured.email || '', 380, 690, { size: 7 });

    // ============ SECTION B - DETAILS OF INSURANCE HISTORY ============
    const insuranceHistory = data.insuranceHistory;
    
    // a) Currently covered by any other Mediclaim / Health Insurance: Yes/No
    drawCheckbox(insuranceHistory.currentlyCoveredByOther === true, 185, 660, { size: 8 });
    drawCheckbox(insuranceHistory.currentlyCoveredByOther === false, 208, 660, { size: 8 });
    
    // b) Date of commencement of first Insurance without break: DD MM YYYY
    if (insuranceHistory.firstInsuranceDate) {
      const dateParts = insuranceHistory.firstInsuranceDate.split('/');
      if (dateParts.length === 3) {
        drawCharacterBoxes(dateParts[0], 368, 663, { size: 7 }); // DD
        drawCharacterBoxes(dateParts[1], 403, 663, { size: 7 }); // MM
        drawCharacterBoxes(dateParts[2], 439, 663, { size: 7 }); // YYYY
      }
    }
    
    // c) If yes, company name
    if (insuranceHistory.otherInsuranceCompanyName) {
      drawCharacterBoxes(insuranceHistory.otherInsuranceCompanyName, 81, 649, { size: 7 });
    }
    
    // Policy No.
    if (insuranceHistory.otherPolicyNo) {
      drawCharacterBoxes(insuranceHistory.otherPolicyNo, 283, 649, { size: 7 });
    }
    
    // Sum insured (Rs.)
    if (insuranceHistory.sumInsuredRs) {
      drawCharacterBoxes(insuranceHistory.sumInsuredRs, 68, 636, { size: 7 });
    }
    
    // d) Have you been hospitalized in the last four years? Yes/No
    drawCheckbox(insuranceHistory.hospitalizedInLastFourYears === true, 365, 636, { size: 8 });
    drawCheckbox(insuranceHistory.hospitalizedInLastFourYears === false, 388, 636, { size: 8 });
    
    // Date (if hospitalized) - MM YY format
    if (insuranceHistory.hospitalizationDate) {
      const hospDateParts = insuranceHistory.hospitalizationDate.split('/');
      if (hospDateParts.length === 3) {
        drawCharacterBoxes(hospDateParts[1], 452, 637, { size: 7 }); // MM
        drawCharacterBoxes(hospDateParts[2].slice(-2), 489, 637, { size: 7 }); // YY
      }
    }
    
    // Diagnosis details
    drawText(insuranceHistory.diagnosisDetails || '', 70, 623, { size: 7 });
    
    // e) Previously covered by any other Mediclaim/Health insurance: Yes/No
    drawCheckbox(insuranceHistory.previouslyCoveredByOther === true, 511, 624, { size: 8 });
    drawCheckbox(insuranceHistory.previouslyCoveredByOther === false, 534, 624, { size: 8 });
    
    // f) If yes, company name
    if (insuranceHistory.previousCompanyName) {
      drawCharacterBoxes(insuranceHistory.previousCompanyName, 91, 610, { size: 7 });
    }

    // ============ SECTION C - DETAILS OF INSURED PERSON HOSPITALIZED ============
    const patientDetails = data.patientDetails;
    
    // a) Name
    drawCharacterBoxes(patientDetails.name, 55, 582, { size: 7 });
    
    // b) Gender checkboxes
    drawCheckbox(patientDetails.gender === 'Male', 101, 569, { size: 8 });
    drawCheckbox(patientDetails.gender === 'Female', 137, 569, { size: 8 });
    
    // c) Age years
    drawCharacterBoxes(patientDetails.age, 200, 569, { size: 7 });
    
    // Age months
    drawCharacterBoxes(patientDetails.ageMonths, 247, 569, { size: 7 });
    
    // d) Date of Birth DD MM YYYY
    if (patientDetails.dateOfBirth) {
      const dobParts = patientDetails.dateOfBirth.split('/');
      if (dobParts.length === 3) {
        drawCharacterBoxes(dobParts[0], 320, 570, { size: 7 }); // DD
        drawCharacterBoxes(dobParts[1], 355, 570, { size: 7 }); // MM
        drawCharacterBoxes(dobParts[2], 392, 570, { size: 7 }); // YYYY
      }
    }
    
    // e) Relationship to Primary insured checkboxes
    const relationship = patientDetails.relationship || 'Self';
    drawCheckbox(relationship === 'Self' || relationship === 'self', 137, 555, { size: 8 });
    drawCheckbox(relationship === 'Spouse' || relationship === 'spouse', 186, 555, { size: 8 });
    drawCheckbox(relationship === 'Child' || relationship === 'child', 222, 555, { size: 8 });
    drawCheckbox(relationship === 'Father' || relationship === 'father', 258, 555, { size: 8 });
    drawCheckbox(relationship === 'Mother' || relationship === 'mother', 306, 555, { size: 8 });
    drawCheckbox(relationship === 'Other' || relationship === 'other', 341, 555, { size: 8 });
    
    // If Other, specify
    if ((relationship === 'Other' || relationship === 'other') && patientDetails.relationshipOther) {
      drawText(patientDetails.relationshipOther, 405, 558, { size: 7 });
    }
    
    // f) Occupation checkboxes
    const occupation = patientDetails.occupation || '';
    drawCheckbox(occupation === 'Service' || occupation === 'service', 101, 542, { size: 8 });
    drawCheckbox(occupation === 'Self Employed' || occupation === 'self_employed', 162, 542, { size: 8 });
    drawCheckbox(occupation === 'Home Maker' || occupation === 'home_maker', 210, 542, { size: 8 });
    drawCheckbox(occupation === 'Student' || occupation === 'student', 258, 542, { size: 8 });
    drawCheckbox(occupation === 'Retired' || occupation === 'retired', 306, 542, { size: 8 });
    drawCheckbox(occupation === 'Other' || occupation === 'other', 341, 542, { size: 8 });
    
    // If Other occupation, specify
    if ((occupation === 'Other' || occupation === 'other') && patientDetails.occupationOther) {
      drawText(patientDetails.occupationOther, 405, 543, { size: 7 });
    }
    
    // g) Address (if different from above)
    drawCharacterBoxes(patientDetails.address || '', 117, 528, { size: 7 });
    drawCharacterBoxes(patientDetails.address2 || '', 54, 515, { size: 7 });
    
    // City
    drawCharacterBoxes(patientDetails.city || '', 66, 503, { size: 7 });
    
    // State
    drawCharacterBoxes(patientDetails.state || '', 318, 503, { size: 7 });
    
    // Pin Code
    drawCharacterBoxes(patientDetails.pinCode || '', 79, 489, { size: 7 });
    
    // Phone No
    drawCharacterBoxes(patientDetails.phoneNo || '', 200, 489, { size: 7 });
    
    // Email ID
    drawText(patientDetails.email || '', 380, 490, { size: 7 });

    // ============ SECTION D - DETAILS OF HOSPITALIZATION ============
    const hospDetails = data.hospitalizationDetails;
    
    // a) Name of Hospital where Admitted
    drawCharacterBoxes(hospDetails.hospitalName || '', 115, 462, { size: 7 });
    
    // b) Room Category occupied checkboxes
    const roomCategory = (hospDetails.roomCategory || '').toLowerCase();
    drawCheckbox(roomCategory === 'day_care' || roomCategory === 'daycare' || roomCategory === 'day care', 161, 448, { size: 7 });
    drawCheckbox(roomCategory === 'single_occupancy' || roomCategory === 'single occupancy' || roomCategory === 'single', 234, 448, { size: 7 });
    drawCheckbox(roomCategory === 'twin_sharing' || roomCategory === 'twin sharing' || roomCategory === 'twin', 305, 448, { size: 7 });
    drawCheckbox(roomCategory === 'three_or_more' || roomCategory === '3 or more beds per room' || roomCategory === 'general', 412, 448, { size: 7 });
    
    // c) Hospitalization due to checkboxes
    const hospDueTo = hospDetails.hospitalizationDueTo || '';
    drawCheckbox(hospDueTo === 'Injury' || hospDueTo === 'injury', 125, 436, { size: 7 });
    drawCheckbox(hospDueTo === 'Illness' || hospDueTo === 'illness', 162, 436, { size: 7 });
    drawCheckbox(hospDueTo === 'Maternity' || hospDueTo === 'maternity', 210, 436, { size: 7 });
    
    // d) Date of injury / Date Disease first detected / Date of Delivery: DD MM YYYY
    const injuryDate = hospDetails.dateOfInjuryOrDisease || hospDetails.dateOfDelivery;
    if (injuryDate) {
      const injDateParts = injuryDate.split('/');
      if (injDateParts.length === 3) {
        drawCharacterBoxes(injDateParts[0], 403, 436, { size: 7 }); // DD
        drawCharacterBoxes(injDateParts[1], 441, 436, { size: 7 }); // MM
        drawCharacterBoxes(injDateParts[2], 477, 436, { size: 7 }); // YYYY
      }
    }
    
    // e) Date of Admission: DD MM YY
    if (hospDetails.dateOfAdmission) {
      const admDateParts = hospDetails.dateOfAdmission.split('/');
      if (admDateParts.length === 3) {
        drawCharacterBoxes(admDateParts[0], 80, 422, { size: 7 }); // DD
        drawCharacterBoxes(admDateParts[1], 116, 422, { size: 7 }); // MM
        drawCharacterBoxes(admDateParts[2].slice(-2), 153, 422, { size: 7 }); // YY
      }
    }
    
    // f) Time of Admission: HH MM
    if (hospDetails.timeOfAdmission) {
      const timeParts = hospDetails.timeOfAdmission.split(':');
      if (timeParts.length >= 2) {
        drawCharacterBoxes(timeParts[0], 211, 422, { size: 7 });
        drawCharacterBoxes(timeParts[1], 247, 422, { size: 7 });
      }
    }
    
    // g) Date of Discharge: DD MM YY
    if (hospDetails.dateOfDischarge) {
      const disDateParts = hospDetails.dateOfDischarge.split('/');
      if (disDateParts.length === 3) {
        drawCharacterBoxes(disDateParts[0], 346, 422, { size: 7 }); // DD
        drawCharacterBoxes(disDateParts[1], 381, 422, { size: 7 }); // MM
        drawCharacterBoxes(disDateParts[2].slice(-2), 416, 422, { size: 7 }); // YY
      }
    }
    
    // h) Time of Discharge: HH : MM
    if (hospDetails.timeOfDischarge) {
      const disTimeParts = hospDetails.timeOfDischarge.split(':');
      if (disTimeParts.length >= 2) {
        drawCharacterBoxes(disTimeParts[0], 476, 422, { size: 7 });
        drawCharacterBoxes(disTimeParts[1], 513, 422, { size: 7 });
      }
    }
    
    // i) If injury give cause checkboxes
    const injuryCause = (hospDetails.injuryCause || '').toLowerCase();
    drawCheckbox(injuryCause === 'self_inflicted' || injuryCause === 'self inflicted', 125, 409, { size: 7 });
    drawCheckbox(injuryCause === 'road_traffic_accident' || injuryCause === 'rta' || injuryCause === 'road traffic accident', 210, 409, { size: 7 });
    drawCheckbox(injuryCause === 'substance_abuse_alcohol' || injuryCause === 'substance_abuse' || injuryCause === 'substance abuse', 353, 409, { size: 7 });
    
    // l) If Medico legal: Yes/No
    drawCheckbox(hospDetails.isMedicoLegal === true, 437, 409, { size: 7 });
    drawCheckbox(hospDetails.isMedicoLegal === false, 461, 409, { size: 7 });
    
    // ii) Reported to Police checkbox
    drawCheckbox(hospDetails.reportedToPolice === true, 90, 395, { size: 7 });
    drawCheckbox(hospDetails.reportedToPolice === false, 113, 395, { size: 7 });
    
    // iii) MLC Report & Police FIR attached: Yes/No
    drawCheckbox(hospDetails.mlcReportAttached === true, 246, 395, { size: 7 });
    drawCheckbox(hospDetails.mlcReportAttached === false, 269, 395, { size: 7 });
    
    // j) System of Medicine
    drawText(hospDetails.systemOfMedicine || '', 369, 396, { size: 7 });
    
  
    // ============ SECTION E - DETAILS OF CLAIM ============
    const expenseDetails = data.expenseDetails;
    const claimDocuments = data.claimDocuments;
    
    // a) Details of Treatment expenses claimed
    // i. Pre-hospitalization expenses
    drawCharacterBoxes(expenseDetails.preHospitalizationExpenses, 140, 355, { size: 8 });
    
    // ii. Hospitalization expenses
    drawCharacterBoxes(expenseDetails.hospitalizationExpenses, 331, 355, { size: 8 });
    
    // iii. Post-hospitalization expenses
    drawCharacterBoxes(expenseDetails.postHospitalizationExpenses, 140, 342, { size: 8 });
    
    // iv. Health-Check up cost
    drawCharacterBoxes(expenseDetails.healthCheckUpCost, 331, 342, { size: 8 });
    
    // v. Ambulance Charges
    drawCharacterBoxes(expenseDetails.ambulanceCharges, 140, 329, { size: 8 });
    
    // vi. Others (code)
    drawCharacterBoxes(expenseDetails.othersCode, 275, 329, { size: 8 });
    // vi. Others Rs.
    drawCharacterBoxes(expenseDetails.othersAmount, 331, 329, { size: 8 });
    
    // Total expenses
    drawCharacterBoxes(expenseDetails.totalExpenses, 331, 316, { size: 8 });
    
    // vii. Pre-hospitalization period: days
    drawCharacterBoxes(expenseDetails.preHospitalizationPeriodDays, 140, 303, { size: 8 });
    
    // viii. Post-hospitalization period: days
    drawCharacterBoxes(expenseDetails.postHospitalizationPeriodDays, 343, 303, { size: 8 });
    
    // b) Claim for Domiciliary Hospitalization: Yes/No
    drawCheckbox(expenseDetails.domiciliaryHospitalization === true, 151, 289, { size: 7 });
    drawCheckbox(expenseDetails.domiciliaryHospitalization === false, 174, 289, { size: 7 });
    
    // c) Details of Lump sum / cash benefit claimed
    // i. Hospital Daily cash
    drawCharacterBoxes(expenseDetails.hospitalDailyCash, 140, 263, { size: 8 });
    
    // ii. Surgical Cash
    drawCharacterBoxes(expenseDetails.surgicalCash, 331, 263, { size: 8 });
    
    // iii. Critical Illness benefit
    drawCharacterBoxes(expenseDetails.criticalIllnessBenefit, 140, 249, { size: 8 });
    
    // iv. Convalescence
    drawCharacterBoxes(expenseDetails.convalescence, 331, 249, { size: 8 });
    
    // v. Pre/Post hospitalization Lump sum benefit
    drawCharacterBoxes(expenseDetails.prePostHospLumpSum, 140, 236, { size: 8 });
    
    // vi. Others code
    drawCharacterBoxes(expenseDetails.otherLumpSumCode, 275, 236, { size: 8 });
    // vi. Others amount
    drawCharacterBoxes(expenseDetails.othersLumpSum, 331, 236, { size: 8 });
    
    // Total lump sum
    drawCharacterBoxes(expenseDetails.totalLumpSum, 331, 223, { size: 8 });
    
    // Claim Documents Submitted - Check List (right side)
    drawCheckbox(claimDocuments.claimFormSigned === true, 436, 356, { size: 7 });
    drawCheckbox(claimDocuments.claimIntimationCopy === true, 436, 344, { size: 7 });
    drawCheckbox(claimDocuments.hospitalMainBill === true, 436, 333, { size: 7 });
    drawCheckbox(claimDocuments.hospitalBreakupBill === true, 436, 321, { size: 7 });
    drawCheckbox(claimDocuments.hospitalBillPaymentReceipt === true, 436, 309, { size: 7 });
    drawCheckbox(claimDocuments.hospitalDischargeSummary === true, 436, 298, { size: 7 });
    drawCheckbox(claimDocuments.pharmacyBill === true, 436, 286, { size: 7 });
    drawCheckbox(claimDocuments.operationTheaterNotes === true, 436, 274, { size: 7 });
    drawCheckbox(claimDocuments.ecg === true, 436, 263, { size: 7 });
    drawCheckbox(claimDocuments.doctorRequestForInvestigation === true, 436, 252, { size: 7 });
    drawCheckbox(claimDocuments.investigationReports === true, 436, 241, { size: 7 });
    drawCheckbox(claimDocuments.doctorsPrescriptions === true, 436, 230, { size: 7 });
    drawCheckbox(claimDocuments.others === true, 436, 219, { size: 7 });

    // ============ SECTION F - DETAILS OF BILLS ENCLOSED ============
    const billsEnclosed = data.billsEnclosed || [];
    
    // Row positions for bills table (up to 10 rows)
    const billRowStartY = 189;
    const rowHeight = 10;
    
    // Column X positions
    const billCols = {
      billNo: 55,
      dateDD: 92,
      dateMM: 119,
      dateYY: 147,
      issuedBy: 185,
      towards: 275,
      amount: 433
    };
    
    // Loop through bills (max 10)
    const maxBills = Math.min(billsEnclosed.length, 10);
    for (let i = 0; i < maxBills; i++) {
      const bill = billsEnclosed[i];
      const rowY = billRowStartY - (i * rowHeight);
      
      // Skip empty bills
      if (!bill || (!bill.billNo && !bill.date && !bill.amount)) continue;
      
      // Bill No.
      drawText(bill.billNo || '', billCols.billNo, rowY, { size: 6 });
      
      // Date DD MM YY
      const billDate = bill.date || '';
      if (billDate) {
        const dateParts = billDate.split('/');
        if (dateParts.length === 3) {
          drawCharacterBoxes(dateParts[0], billCols.dateDD - 10, rowY, { size: 7 });
          drawCharacterBoxes(dateParts[1], billCols.dateMM - 10, rowY, { size: 7 });
          drawCharacterBoxes(dateParts[2].slice(-2), billCols.dateYY - 10, rowY, { size: 7 });
        }
      }
      
      // Issued by
      drawText(bill.issuedBy || '', billCols.issuedBy, rowY, { size: 7 });
      
      // Towards
      drawText(bill.towards || '', billCols.towards, rowY, { size: 7 });
      
      // Amount
      drawCharacterBoxes(String(bill.amount || ''), billCols.amount, rowY, 14, { size: 8 });
    }

    // ============ SECTION G - DETAILS OF PRIMARY INSURED'S BANK ACCOUNT ============
    const bankDetails = data.bankDetails;
    
    // a) PAN
    drawCharacterBoxes(bankDetails.pan || '', 55, 75, { size: 7 });
    
    // b) Account Number
    drawCharacterBoxes(bankDetails.accountNumber || '', 259, 75, { size: 7 });
    
    // c) Bank Name and Branch
    const bankNameBranch = ((bankDetails.bankName || '') + ' ' + (bankDetails.branchName || '')).trim();
    drawCharacterBoxes(bankNameBranch, 91, 61, 12, { size: 7 });
    
    // d) Cheque / DD Payable details
    drawText(bankDetails.chequeOrDDPayableDetails || '', 115, 48, { size: 7 });
    
    // e) IFSC Code
    drawCharacterBoxes(bankDetails.ifscCode || '', 357, 48, { size: 7 });

    // ============ PAGE 2 ============
    // ============ SECTION H - DECLARATION BY THE INSURED ============
    const declaration = data.declaration;
    
    // Helper functions for second page
    const drawTextPage2 = (text, x, y, options = {}) => {
      drawTextOnPage(secondPage, text, x, y, options);
    };
    
    const drawCharacterBoxesPage2 = (text, startX, y, charWidthOrOptions = 10.75, options = {}) => {
      drawCharacterBoxesOnPage(secondPage, text, startX, y, charWidthOrOptions, options);
    };
    
    // Date DD MM YYYY
    if (declaration.date) {
      const dateParts = declaration.date.split('/');
      if (dateParts.length === 3) {
        drawCharacterBoxesPage2(dateParts[0], 50, 711, { size: 7 }); // DD
        drawCharacterBoxesPage2(dateParts[1], 86, 711, { size: 7 }); // MM
        drawCharacterBoxesPage2(dateParts[2], 120, 711, { size: 7 }); // YYYY
      }
    }
    
    // Place
    drawTextPage2(declaration.place || '', 203, 712, { size: 7 });

    // Save and return the PDF bytes
    const pdfBytes = await pdfDoc.save();
    return pdfBytes;

  } catch (error) {
    console.error('Error generating Form A PDF:', error);
    throw error;
  }
};

/**
 * Download the claim form A as PDF directly
 */
export const downloadClaimFormAPdf = async (data, filename = 'ClaimFormA.pdf') => {
  try {
    const pdfBytes = await generateClaimFormAPdf(data);
    
    // Create blob and download
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return pdfBytes;
  } catch (error) {
    console.error('Error downloading Form A PDF:', error);
    throw error;
  }
};

// Export transform function for testing
export const transformClaimFormAData = transformData;

export default generateClaimFormAPdf;
